import React, {useState, useEffect, useCallback} from 'react';
import {Modal} from "react-bootstrap";
import {Alert, Button} from "fearless-common-ui";
import {BackendForm, FloatingLabelField} from "./forms";
import {backendEndpoints} from "../libs/backendEndpoints";

const PasswordConfirmationContext = React.createContext({
    open: ({ onSuccess }: { onSuccess: () => void }) => {},
});

/**
 * Provides a React context and modal-based UI for password confirmation.
 * Allows components to trigger a password confirmation flow by invoking the `open` method from the context.
 *
 * @param {Object} props - The props passed to the provider component.
 * @param {React.ReactNode} props.children - The child components wrapped by this provider.
 * @return {React.ReactElement} The provider component that wraps around its children and manages the password confirmation flow.
 */
export function PasswordConfirmationProvider({ children }: { children: React.ReactNode }) {
    const [show, setShow] = useState<false | { onSuccess: () => void }>(false);
    const [error, setError] = useState('');

    const handleClose = () => {
      setShow(false);
      setError('');
    };

    const open = useCallback(({ onSuccess }: { onSuccess: () => void }) => {
      setShow({ onSuccess });
    }, []);

    return (
      <PasswordConfirmationContext.Provider value={{ open }}>
          {children}
          {error && (
            <Modal show onHide={handleClose} centered animation={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert>{error}</Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </Modal>
          )}
          {show && (
            <Modal show onHide={handleClose} centered animation={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>This action requires you to confirm your identity with your password:</p>
                    <BackendForm<{ password: string }>
                      initialValues={{ password: '' }}
                      config={{
                        withCredentials: true,
                        withXSRFToken: true,
                      }}
                      targetEndpoint={backendEndpoints.confirmPassword}
                      targetMethod="POST"
                      onSuccess={() => {
                          show.onSuccess();
                          handleClose();
                      }}
                    >
                        <FloatingLabelField
                          name="password"
                          type="password"
                          label="Password"
                          autoFocus
                        />
                    </BackendForm>
                </Modal.Body>
            </Modal>
          )}
      </PasswordConfirmationContext.Provider>
    );
}


export default function PasswordConfirmationButton({ variant, buttonText, onSuccess }: {
    buttonText: string;
    variant: 'primary' | 'danger';
    onSuccess: () => Promise<void>;
}) {
    const { open } = React.useContext(PasswordConfirmationContext);

    useEffect(() => {
        // Need to be deleted, because otherwise Sanctum Guard will try to set the request's group
        // which will create the request group token using TransientToken, but we need it to use the
        // App\Model\PersonalAccessToken. See Laravel\Sanctum\Guard::__invoke method.
        // Cookies.remove('XSRF-TOKEN');
        // Cookies.remove('fearless_league_session');
    }, []);

    return (
        <>
            <Button variant={variant} onClick={() => open({ onSuccess })}>
                {buttonText}
            </Button>
        </>
    );
}
